import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  X, Package, Plus, Play, Pause, Save, Trash2, FolderPlus 
} from 'lucide-react';

export const InstrumentPackageBuilder = ({ 
  isOpen, 
  onClose, 
  userSamples, 
  packages, 
  onSavePackage, 
  onDeletePackage 
}) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [packageName, setPackageName] = useState('');
  const [packageColor, setPackageColor] = useState('#8b5cf6');
  const [subInstruments, setSubInstruments] = useState([]);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  
  const API = process.env.REACT_APP_BACKEND_URL || '';
  
  // Reset when opening
  useEffect(() => {
    if (isOpen) {
      setSelectedPackage(null);
      setEditMode(false);
      setPackageName('');
      setSubInstruments([]);
    }
  }, [isOpen]);
  
  // Load package for editing
  const loadPackageForEdit = (pkg) => {
    setSelectedPackage(pkg);
    setEditMode(true);
    setPackageName(pkg.name);
    setPackageColor(pkg.color || '#8b5cf6');
    setSubInstruments(pkg.subInstruments || []);
  };
  
  // Create new package
  const createNewPackage = () => {
    setSelectedPackage(null);
    setEditMode(true);
    setPackageName('New Package');
    setPackageColor('#8b5cf6');
    setSubInstruments([{ id: `sub_${Date.now()}`, name: 'Instrument 1', samples: [] }]);
  };
  
  // Add sub-instrument
  const addSubInstrument = () => {
    setSubInstruments(prev => [...prev, { 
      id: `sub_${Date.now()}`, 
      name: `Instrument ${prev.length + 1}`, 
      samples: [] 
    }]);
  };
  
  // Remove sub-instrument
  const removeSubInstrument = (subId) => {
    setSubInstruments(prev => prev.filter(s => s.id !== subId));
  };
  
  // Update sub-instrument name
  const updateSubInstrumentName = (subId, name) => {
    setSubInstruments(prev => prev.map(s => s.id === subId ? { ...s, name } : s));
  };
  
  // Add sample to sub-instrument
  const addSampleToSubInstrument = (subId, sampleId) => {
    const sample = userSamples[sampleId];
    if (!sample) return;
    
    setSubInstruments(prev => prev.map(s => {
      if (s.id === subId) {
        // Don't add duplicate
        if (s.samples.find(sp => sp.id === sampleId)) return s;
        return {
          ...s,
          samples: [...s.samples, {
            id: sampleId,
            name: sample.name,
            url: sample.url,
            keyswitch: `C${s.samples.length + 1}` // Auto-assign keyswitch
          }]
        };
      }
      return s;
    }));
  };
  
  // Remove sample from sub-instrument
  const removeSampleFromSubInstrument = (subId, sampleId) => {
    setSubInstruments(prev => prev.map(s => {
      if (s.id === subId) {
        return { ...s, samples: s.samples.filter(sp => sp.id !== sampleId) };
      }
      return s;
    }));
  };
  
  // Preview sample
  const previewSample = (url) => {
    if (previewAudio) {
      previewAudio.pause();
    }
    
    if (playingId === url) {
      setPlayingId(null);
      setPreviewAudio(null);
      return;
    }
    
    const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
    const audio = new Audio(fullUrl);
    audio.volume = 0.7;
    audio.onended = () => {
      setPlayingId(null);
      setPreviewAudio(null);
    };
    audio.play();
    setPreviewAudio(audio);
    setPlayingId(url);
  };
  
  // Save package
  const handleSave = () => {
    if (!packageName.trim()) {
      toast.error('Package name required');
      return;
    }
    
    if (subInstruments.length === 0) {
      toast.error('Add at least one sub-instrument');
      return;
    }
    
    const pkg = {
      id: selectedPackage?.id || `pkg_${Date.now()}`,
      name: packageName,
      color: packageColor,
      subInstruments,
      createdAt: selectedPackage?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    onSavePackage(pkg);
    setEditMode(false);
    setSelectedPackage(null);
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-[#1a1a1a] rounded-xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#333]">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-indigo-400" />
            <h2 className="text-xl font-bold">Instrument Packages</h2>
          </div>
          <div className="flex items-center gap-2">
            <Link 
              to="/admin/kit-samples" 
              className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 rounded-lg text-xs font-medium flex items-center gap-1"
            >
              <FolderPlus className="w-4 h-4" /> Upload Kit Samples
            </Link>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 flex overflow-hidden">
          {/* Package List (Left) */}
          <div className="w-64 bg-[#0a0a0a] border-r border-[#333] flex flex-col">
            <div className="p-3 border-b border-[#333]">
              <button
                onClick={createNewPackage}
                className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                <FolderPlus className="w-4 h-4" /> New Package
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-2">
              {packages.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-8">
                  No packages yet.<br />Create your first one!
                </div>
              )}
              
              {packages.map(pkg => (
                <div
                  key={pkg.id}
                  className={`p-3 rounded-lg mb-2 cursor-pointer transition-colors ${
                    selectedPackage?.id === pkg.id ? 'bg-indigo-500/20 border border-indigo-500' : 'bg-[#1a1a1a] hover:bg-[#252525] border border-transparent'
                  }`}
                  onClick={() => loadPackageForEdit(pkg)}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: pkg.color || '#8b5cf6' }} />
                    <span className="font-medium truncate">{pkg.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {pkg.subInstruments?.length || 0} instruments
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Editor (Right) */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {!editMode ? (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Select a package to edit</p>
                  <p className="text-sm mt-1">or create a new one</p>
                </div>
              </div>
            ) : (
              <>
                {/* Package Info */}
                <div className="p-4 border-b border-[#333] flex items-center gap-4">
                  <input
                    type="color"
                    value={packageColor}
                    onChange={(e) => setPackageColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={packageName}
                    onChange={(e) => setPackageName(e.target.value)}
                    className="flex-1 bg-[#0a0a0a] text-white text-xl font-bold px-4 py-2 rounded-lg border border-[#333] focus:border-indigo-500 focus:outline-none"
                    placeholder="Package Name"
                  />
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" /> Save
                  </button>
                  {selectedPackage && (
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this package?')) {
                          onDeletePackage(selectedPackage.id);
                          setEditMode(false);
                        }
                      }}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Sub-instruments */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium">Sub-Instruments</h3>
                    <button
                      onClick={addSubInstrument}
                      className="px-3 py-1.5 bg-[#333] hover:bg-[#444] rounded-lg text-sm flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Add Instrument
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {subInstruments.map((sub, idx) => (
                      <div key={sub.id} className="bg-[#0a0a0a] rounded-lg p-4 border border-[#333]">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-gray-500 text-sm">#{idx + 1}</span>
                          <input
                            type="text"
                            value={sub.name}
                            onChange={(e) => updateSubInstrumentName(sub.id, e.target.value)}
                            className="flex-1 bg-[#1a1a1a] text-white px-3 py-1.5 rounded border border-[#333] focus:border-indigo-500 focus:outline-none"
                            placeholder="Instrument Name"
                          />
                          <button
                            onClick={() => removeSubInstrument(sub.id)}
                            className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/20 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Samples in this sub-instrument */}
                        <div className="mb-3">
                          <div className="text-xs text-gray-500 mb-2">Assigned Samples:</div>
                          {sub.samples.length === 0 ? (
                            <div className="text-xs text-gray-600 italic">No samples. Select from the list below.</div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {sub.samples.map(sample => (
                                <div
                                  key={sample.id}
                                  className="flex items-center gap-1 bg-[#1a1a1a] px-2 py-1 rounded text-xs border border-[#333]"
                                >
                                  <button
                                    onClick={() => previewSample(sample.url)}
                                    className={`p-0.5 ${playingId === sample.url ? 'text-green-400' : 'text-gray-400 hover:text-green-400'}`}
                                  >
                                    {playingId === sample.url ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                                  </button>
                                  <span className="truncate max-w-[120px]">{sample.name}</span>
                                  <span className="text-indigo-400 ml-1">[{sample.keyswitch}]</span>
                                  <button
                                    onClick={() => removeSampleFromSubInstrument(sub.id, sample.id)}
                                    className="ml-1 text-gray-500 hover:text-red-400"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Sample picker */}
                        <div className="border-t border-[#333] pt-3">
                          <div className="text-xs text-gray-500 mb-2">Add samples from library:</div>
                          <div className="max-h-32 overflow-y-auto bg-[#141414] rounded p-2">
                            {Object.entries(userSamples).slice(0, 50).map(([id, sample]) => {
                              const isAdded = sub.samples.find(s => s.id === id);
                              return (
                                <button
                                  key={id}
                                  onClick={() => addSampleToSubInstrument(sub.id, id)}
                                  disabled={isAdded}
                                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-2 ${
                                    isAdded 
                                      ? 'text-gray-600 cursor-not-allowed' 
                                      : 'text-gray-300 hover:bg-white/10'
                                  }`}
                                >
                                  <button
                                    onClick={(e) => { e.stopPropagation(); previewSample(sample.url); }}
                                    className="p-0.5 text-gray-400 hover:text-green-400"
                                  >
                                    <Play className="w-3 h-3" />
                                  </button>
                                  <span className="truncate">{sample.name}</span>
                                  {isAdded && <span className="text-green-500 ml-auto">Added</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default InstrumentPackageBuilder;
